import { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'

import {
  useOverlayState,
  removeOverlay,
  setChyron,
} from '../../firebase/hooks/useOverlays'
import { useDebounce } from '../../sharedLegacy/hooks'
import { ChyronType } from '../../firebase/model/chyron-model'

type Poll = {
  cardId?: string
  question: string
  opened_date: string
  answer_count?: string
  open_p: number
  poll_id: string
  option_count?: string
  options: {
    poll_option_id: string
    answer_percentage: string
    option: string
  }[]
}

type PollsState = {
  'feature-webinar-main-sidebar-polls'?: {
    polls?: Poll[]
  }
}

const formatPoll = (poll: Poll) => {
  return {
    cardId: poll.cardId,
    question: poll.question,
    isOpen: !!poll.open_p,
    id: poll.poll_id,
    options: poll.options.map((option) => ({
      id: +option.poll_option_id,
      answerPercentage: +option.answer_percentage,
      option: option.option,
    })),
  }
}

const usePollsOverlays = () => {
  const overlay = useOverlayState()
  const activePollId = overlay?.type === ChyronType.PollResult && overlay.id
  const activeOverlayCardId =
    overlay?.type === ChyronType.PollResult && overlay.cardId
  const mainActivePolls = useSelector<PollsState, Poll[] | undefined>(
    (state) => state['feature-webinar-main-sidebar-polls']?.polls
  )

  const setPollOverlay = useCallback((poll: Poll) => {
    void setChyron({
      type: ChyronType.PollResult,
      ...formatPoll(poll),
    })
  }, [])

  const togglePollOverlay = useCallback(
    (poll: Poll) => {
      if (
        activePollId !== poll.poll_id ||
        activeOverlayCardId !== poll.cardId
      ) {
        setPollOverlay(poll)
      } else {
        void removeOverlay()
      }
    },
    [activeOverlayCardId, activePollId, setPollOverlay]
  )

  const debounceRealTimeUpdate = useDebounce((poll: Poll) => {
    setPollOverlay(poll)
  }, 1000)

  /**
   * TODO (kasperkronborg): I'm not even sure why this is here? Be the looks of
   *  things, this is not called continuously.
   */
  /*
   * Listen for realtime updates on the active polls and update Firebase with lastest data for the selected one in the overlay
   */
  useEffect(() => {
    const activeOverlayPoll = mainActivePolls?.find(
      (poll) => poll.poll_id === activePollId
    )

    if (overlay?.type !== ChyronType.PollResult || !activeOverlayPoll) {
      debounceRealTimeUpdate.cancel()
      return
    }

    debounceRealTimeUpdate({
      ...activeOverlayPoll,
      cardId: activeOverlayCardId || undefined,
    })
  }, [
    activeOverlayCardId,
    activePollId,
    debounceRealTimeUpdate,
    mainActivePolls,
    overlay?.type,
  ])

  return { togglePollOverlay, overlayPollId: activePollId }
}

// eslint-disable-next-line import-x/no-default-export
export default usePollsOverlays
